import { useQuery } from "@tanstack/react-query";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal } from "@/components/motion/Reveal";
import { ProgrammeCard } from "@/components/learning/ProgrammeCard";
import { api } from "@/lib/api";

export default function Upcoming() {
  const { data: programmes = [], isLoading } = useQuery({ queryKey: ["training"], queryFn: async () => (await api.get("/training")).data });
  const open = programmes.filter((p) => p.status === "OFFERED" || p.status === "FULL");
  const dated = programmes.filter((p) => p.upcoming_dates?.length > 0 && !open.includes(p));
  return (
    <>
      <Seo title="Upcoming Programmes" path="/learning/upcoming" description="Planet Trotter programmes currently open for registration, and editions with announced dates. Programmes appear here only when they are genuinely offered." />
      <PageHero
        eyebrow="Field Learning · Upcoming"
        titleLines={["Open for", "registration."]}
        intro="Only programmes marked Currently Offered appear here. Nothing is listed as available unless it is."
        image="/dawn/vor-raft-group.jpg"
      />
      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          {isLoading && <p className="text-ptf-text/60">Loading.</p>}
          {!isLoading && open.length === 0 && dated.length === 0 && (
            <Reveal>
              <div data-testid="no-upcoming" className="bg-white border border-ptf-primary/12 p-10 max-w-2xl">
                <h2 className="font-display uppercase text-3xl tracking-tight text-ptf-primary">No open editions right now.</h2>
                <p className="mt-4 text-ptf-text/70 leading-relaxed">
                  The first public editions are being scheduled. Browse the catalogue and
                  enquire on any programme: enquiries directly shape which editions open
                  first, and you will be written to the moment yours does.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <PtfButton to="/learning/training" data-testid="upcoming-browse-cta">Browse the Catalogue</PtfButton>
                  <PtfButton to="/learning/institutional" variant="outline">Request a Group Programme</PtfButton>
                </div>
              </div>
            </Reveal>
          )}
          {open.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="upcoming-grid">
              {open.map((p) => <ProgrammeCard key={p.slug} p={p} />)}
            </div>
          )}
          {dated.length > 0 && (
            <div className="mt-12">
              <p className="text-[0.68rem] uppercase tracking-[0.2em] text-ptf-text/55 mb-4">Editions with announced dates</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {dated.map((p) => <ProgrammeCard key={p.slug} p={p} />)}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
